import { ethers } from 'ethers';
import { supabase } from './supabaseClient';

export const STALE_WITHDRAWAL_AFTER_MS = 10 * 60 * 1000;

type WithdrawalRequestRow = {
  id: string;
  status: string;
  actual_amount_base_units: string;
  requested_at: string;
  claim_tx_hash: string | null;
  claim_block_number: number | null;
  claim_log_index: number | null;
  claim_nonce: number | null;
  deadline: number | null;
  signature_v: number | null;
  signature_r: string | null;
  signature_s: string | null;
  signed_at: string | null;
  claimed_at: string | null;
  context: string | null;
  wallet_transaction_id: string | null;
  claim_contract_address: string;
  destination_address: string;
};

type TokenConfigRow = { id: string; symbol: string; decimals: number };
export type WithdrawalClaimCheck = 'not_found' | 'found' | 'unknown';

export type PendingWithdrawal = {
  id: string;
  status: string;
  amount: string;
  requestedAt: string;
  isStale: boolean;
  claimCheck: WithdrawalClaimCheck;
  canCancel: boolean;
};

function ensureSupabase() {
  if (!supabase) throw new Error('Supabase 尚未設定。');
  return supabase;
}

function hasClaim(request: WithdrawalRequestRow): boolean {
  return Boolean(
    request.claim_tx_hash || request.claim_block_number !== null || request.claim_log_index !== null
      || request.claim_nonce !== null || request.deadline !== null || request.signature_v
      || request.signature_r || request.signature_s || request.signed_at || request.claimed_at
      || request.context || request.wallet_transaction_id,
  );
}

async function getToken(): Promise<TokenConfigRow> {
  const client = ensureSupabase();
  const { data, error } = await client.schema('api').rpc('get_active_token_configs_v1');
  if (error) throw error;
  const token = (Array.isArray(data) ? data : []).find((entry: { symbol?: string }) => entry.symbol === 'BBG') as TokenConfigRow | undefined;
  if (!token) throw new Error('找不到 BBG token 設定。');
  return token;
}

async function getRows(token: TokenConfigRow, requestId: string | null = null): Promise<WithdrawalRequestRow[]> {
  const client = ensureSupabase();
  const { data, error } = await client.schema('api').rpc('get_my_pending_withdrawals_v1', {
    p_token_symbol: token.symbol,
    p_request_id: requestId,
  });
  if (error) throw error;
  return Array.isArray(data) ? data as WithdrawalRequestRow[] : [];
}

function mapWithdrawal(request: WithdrawalRequestRow, decimals: number, claimCheck: WithdrawalClaimCheck): PendingWithdrawal {
  const isStale = Date.now() - Date.parse(request.requested_at) >= STALE_WITHDRAWAL_AFTER_MS;
  return {
    id: request.id,
    status: request.status,
    amount: ethers.formatUnits(request.actual_amount_base_units, decimals),
    requestedAt: request.requested_at,
    isStale,
    claimCheck,
    canCancel: request.status === 'requested' && isStale && !hasClaim(request) && claimCheck === 'not_found',
  };
}

async function checkClaims(requestIds: string[]): Promise<Map<string, WithdrawalClaimCheck>> {
  if (requestIds.length === 0) return new Map();
  const client = ensureSupabase();
  const { data, error } = await client.functions.invoke('check-stale-withdrawals', { body: { request_ids: requestIds } });
  if (error) throw error;
  const result = new Map<string, WithdrawalClaimCheck>();
  for (const row of Array.isArray(data?.results) ? data.results : []) {
    if (typeof row?.request_id === 'string' && ['found', 'not_found', 'unknown'].includes(row.claim_check)) {
      result.set(row.request_id, row.claim_check as WithdrawalClaimCheck);
    }
  }
  return result;
}

export async function getPendingWithdrawals(): Promise<PendingWithdrawal[]> {
  const token = await getToken();
  const rows = await getRows(token);
  const staleRows = rows.filter((row) => row.status === 'requested' && Date.now() - Date.parse(row.requested_at) >= STALE_WITHDRAWAL_AFTER_MS && !hasClaim(row));
  try {
    const claims = await checkClaims(staleRows.map((row) => row.id));
    return rows.map((row) => mapWithdrawal(row, token.decimals, claims.get(row.id) ?? 'unknown'));
  } catch {
    return rows.map((row) => mapWithdrawal(row, token.decimals, 'unknown'));
  }
}

export async function cancelStaleWithdrawal(requestId: string): Promise<void> {
  const client = ensureSupabase();
  const token = await getToken();
  const request = (await getRows(token, requestId)).find((row) => row.id === requestId);
  if (!request) throw new Error('提款申請不存在。');
  const pending = mapWithdrawal(request, token.decimals, 'unknown');
  if (!pending.canCancel) {
    if (request.status !== 'requested') throw new Error('這筆提款已經進入其他處理狀態，不能取消。');
    if (!pending.isStale) throw new Error('提款仍在正常處理時間內，請稍候再試。');
    if (hasClaim(request)) throw new Error('這筆提款已有鏈上交易紀錄，不能取消。');
    const claims = await checkClaims([requestId]);
    if (claims.get(requestId) !== 'not_found') throw new Error('暫時無法確認鏈上提款狀態，為安全起見未解除凍結。');
  }
  const { data, error } = await client.functions.invoke('cancel-stale-withdrawal', { body: { request_id: requestId } });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || '提款取消失敗。');
}
